import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';

const TYPE_CONFIG = {
  appointment_confirmation: {
    border: 'border-l-blue-500',
    bg: 'bg-white',
    iconBg: 'bg-blue-50',
    iconColor: '#3B82F6',
    icon: 'CalendarCheck',
    label: 'Confirmation RDV',
    action: { label: 'Voir le RDV', path: '/appointments' },
  },
  appointment: {
    border: 'border-l-blue-500',
    bg: 'bg-white',
    iconBg: 'bg-blue-50',
    iconColor: '#3B82F6',
    icon: 'Calendar',
    label: 'Rendez-vous',
    action: { label: 'Gérer', path: '/appointments' },
  },
  property_alert: {
    border: 'border-l-emerald-500',
    bg: 'bg-white',
    iconBg: 'bg-emerald-50',
    iconColor: '#10B981',
    icon: 'BellRing',
    label: 'Alerte Propriété',
    action: { label: 'Voir les biens', path: '/property-search-listings' },
  },
  property_inquiry: {
    border: 'border-l-amber-500',
    bg: 'bg-white',
    iconBg: 'bg-amber-50',
    iconColor: '#F59E0B',
    icon: 'Home',
    label: 'Demande Propriété',
    action: { label: 'Voir la propriété', path: '/property-search-listings' },
  },
  property_update: {
    border: 'border-l-emerald-500',
    bg: 'bg-white',
    iconBg: 'bg-emerald-50',
    iconColor: '#10B981',
    icon: 'RefreshCw',
    label: 'Mise à jour',
    action: { label: 'Voir', path: '/property-search-listings' },
  },
  booking_update: {
    border: 'border-l-violet-500',
    bg: 'bg-white',
    iconBg: 'bg-violet-50',
    iconColor: '#8B5CF6',
    icon: 'BookOpen',
    label: 'Réservation',
    action: { label: 'Voir la réservation', path: '/appointments' },
  },
  notary_appointment: {
    border: 'border-l-purple-500',
    bg: 'bg-white',
    iconBg: 'bg-purple-50',
    iconColor: '#8B5CF6',
    icon: 'Scale',
    label: 'RDV Notaire',
    action: { label: 'Voir le RDV', path: '/notary-booking' },
  },
  document_ready: {
    border: 'border-l-indigo-500',
    bg: 'bg-white',
    iconBg: 'bg-indigo-50',
    iconColor: '#6366F1',
    icon: 'FileText',
    label: 'Document prêt',
    action: { label: 'Télécharger', path: '/account-dashboard' },
  },
  general: {
    border: 'border-l-gray-300',
    bg: 'bg-white',
    iconBg: 'bg-gray-50',
    iconColor: '#6B7280',
    icon: 'Bell',
    label: 'Notification',
    action: { label: 'Voir', path: '/notifications' },
  },
};

const ToastItem = ({ toast, onDismiss }) => {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const config = TYPE_CONFIG?.[toast?.type] || TYPE_CONFIG?.general;

  useEffect(() => {
    // Trigger slide-in
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  const handleDismiss = () => {
    setLeaving(true);
    setTimeout(() => onDismiss?.(toast?.id), 300);
  };

  const handleAction = () => {
    const actionPath = toast?.actionPath || config?.action?.path;
    if (actionPath) navigate(actionPath);
    handleDismiss();
  };

  return (
    <div
      className={`
        flex items-start gap-3 w-[340px] max-w-[calc(100vw-2rem)]
        px-4 py-3.5 rounded-xl border-l-4 shadow-lg
        transition-all duration-300 ease-out
        ${config?.border} ${config?.bg}
        ${visible && !leaving
          ? 'opacity-100 translate-y-0'
          : leaving
            ? 'opacity-0 -translate-y-2' :'opacity-0 -translate-y-4'
        }
      `}
      role="alert"
      aria-live="polite"
    >
      {/* Icon */}
      <div className={`flex-shrink-0 mt-0.5 w-8 h-8 rounded-lg ${config?.iconBg} flex items-center justify-center`}>
        <Icon name={toast?.icon || config?.icon} size={16} color={config?.iconColor} strokeWidth={2} />
      </div>
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: config?.iconColor }}>
            {config?.label}
          </span>
        </div>
        <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-1">
          {toast?.title || config?.label}
        </p>
        {toast?.message && (
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">
            {toast?.message}
          </p>
        )}

        {/* Action CTA */}
        <button
          onClick={handleAction}
          className="mt-2 text-xs font-semibold px-2.5 py-1 rounded-md transition-colors"
          style={{
            color: config?.iconColor,
            backgroundColor: `${config?.iconBg?.replace('bg-', '')}`,
          }}
        >
          <span className="flex items-center gap-1">
            {toast?.actionLabel || config?.action?.label}
            <Icon name="ArrowRight" size={11} color={config?.iconColor} strokeWidth={2.5} />
          </span>
        </button>
      </div>
      {/* Dismiss */}
      <button
        onClick={handleDismiss}
        className="flex-shrink-0 p-1.5 rounded-lg hover:bg-gray-100 transition-colors mt-0.5"
        aria-label="Fermer"
      >
        <Icon name="X" size={13} color="#9CA3AF" strokeWidth={2.5} />
      </button>
    </div>
  );
};

/**
 * Renders a stack of toast notifications at the top-center of the screen.
 * Props:
 *  - toasts   : [{ id, type, title, message, icon?, actionLabel?, actionPath? }]
 *  - onDismiss: fn(id)
 */
const NotificationToast = ({ toasts = [], onDismiss }) => {
  if (!toasts?.length) return null;

  return (
    <div
      className="fixed top-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-2.5 pointer-events-none"
      aria-label="Notifications"
    >
      {toasts?.map((toast) => (
        <div key={toast?.id} className="pointer-events-auto">
          <ToastItem toast={toast} onDismiss={onDismiss} />
        </div>
      ))}
    </div>
  );
};

export default NotificationToast;
